/**
 * Lightweight markdown renderer for chat messages.
 * Returns React nodes — no dangerouslySetInnerHTML.
 *
 * Supported patterns:
 * - ```code block```
 * - `inline code`
 * - **bold** or *bold*
 * - _italic_
 * - ~strikethrough~
 * - URL auto-linking
 */

import type { ReactNode } from 'react';

const URL_REGEX = /https?:\/\/[^\s<]+[^\s<.,;:!?)'"]/g;
const CODE_BLOCK_REGEX = /```([\s\S]*?)```/g;
const INLINE_CODE_REGEX = /`([^`]+)`/g;
const BOLD_REGEX = /\*\*(.+?)\*\*|\*(.+?)\*/g;
const ITALIC_REGEX = /(?<!\*)\b_(.+?)_\b(?!\*)/g;
const STRIKE_REGEX = /~(.+?)~/g;

interface Token {
  type: 'text' | 'code_block' | 'inline_code' | 'bold' | 'italic' | 'strike' | 'link';
  content: string;
  raw: string;
}

function tokenize(text: string): Token[] {
  const tokens: Token[] = [];
  let remaining = text;

  // First pass: extract code blocks (they should not be further parsed)
  const parts: Array<{ type: 'raw' | 'code_block'; content: string }> = [];
  let lastEnd = 0;

  for (const match of text.matchAll(CODE_BLOCK_REGEX)) {
    if (match.index! > lastEnd) {
      parts.push({ type: 'raw', content: text.slice(lastEnd, match.index!) });
    }
    parts.push({ type: 'code_block', content: match[1] });
    lastEnd = match.index! + match[0].length;
  }
  if (lastEnd < text.length) {
    parts.push({ type: 'raw', content: text.slice(lastEnd) });
  }

  // If no code blocks, treat whole string as raw
  if (parts.length === 0) {
    parts.push({ type: 'raw', content: text });
  }

  for (const part of parts) {
    if (part.type === 'code_block') {
      tokens.push({ type: 'code_block', content: part.content.trim(), raw: '```' + part.content + '```' });
      continue;
    }

    // Parse inline formatting from raw text
    remaining = part.content;

    // Build a combined regex to find the earliest match
    const patterns: Array<{ regex: RegExp; type: Token['type'] }> = [
      { regex: INLINE_CODE_REGEX, type: 'inline_code' },
      { regex: BOLD_REGEX, type: 'bold' },
      { regex: ITALIC_REGEX, type: 'italic' },
      { regex: STRIKE_REGEX, type: 'strike' },
      { regex: URL_REGEX, type: 'link' },
    ];

    let cursor = 0;
    const str = remaining;

    while (cursor < str.length) {
      let earliest: { index: number; length: number; content: string; type: Token['type'] } | null = null;

      for (const p of patterns) {
        p.regex.lastIndex = cursor;
        const m = p.regex.exec(str);
        if (m && (earliest === null || m.index < earliest.index)) {
          const content = p.type === 'bold' ? (m[1] || m[2]) :
                         p.type === 'link' ? m[0] :
                         m[1];
          earliest = { index: m.index, length: m[0].length, content, type: p.type };
        }
      }

      if (!earliest) {
        tokens.push({ type: 'text', content: str.slice(cursor), raw: str.slice(cursor) });
        break;
      }

      if (earliest.index > cursor) {
        tokens.push({ type: 'text', content: str.slice(cursor, earliest.index), raw: str.slice(cursor, earliest.index) });
      }

      tokens.push({ type: earliest.type, content: earliest.content, raw: str.slice(earliest.index, earliest.index + earliest.length) });
      cursor = earliest.index + earliest.length;
    }
  }

  return tokens;
}

export function renderMarkdown(text: string): ReactNode[] {
  const tokens = tokenize(text);

  return tokens.map((token, i) => {
    switch (token.type) {
      case 'code_block':
        return (
          <pre key={i} className="chat-markdown-pre my-1 overflow-x-auto rounded-lg bg-black/10 p-2 text-[13px]">
            <code className="font-mono">{token.content}</code>
          </pre>
        );
      case 'inline_code':
        return (
          <code key={i} className="chat-markdown-code rounded bg-black/10 px-1 py-0.5 font-mono text-[13px]">
            {token.content}
          </code>
        );
      case 'bold':
        return <strong key={i}>{token.content}</strong>;
      case 'italic':
        return <em key={i}>{token.content}</em>;
      case 'strike':
        return <del key={i}>{token.content}</del>;
      case 'link':
        return (
          <a
            key={i}
            href={token.content}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline hover:text-blue-800"
          >
            {token.content}
          </a>
        );
      case 'text':
      default:
        return <span key={i}>{token.content}</span>;
    }
  });
}
